import { create } from "zustand";
import api from "../../../utils/axios-utils";
import { isDemoAccount, loadDemoSlideAttempts } from "../service/demoAnalytics";

export type SlideAttempt = {
  slide_idx: number;
  slide_start_ts: number | null;
  slide_end_ts: number | null;
  bite_status: string;
  /** Practice only; null when the W/H classifiers never ran for this attempt. */
  instructional_help: boolean | null;
  tutor_support_level: string | null;
  time_spent_ms: number;
  attempts_total: number;
  attempts_correct: number;
  attempts_incorrect: number;
  student_asked_count: number;
  ai_asked_count: number;
};

type CacheEntry = {
  data: SlideAttempt[];
  timestamp: number;
};

type BiteAttemptsState = {
  cache: Map<string, CacheEntry>;
  /** Bumped each time a fetch starts for a key; lets a request that resolves
   *  late (e.g. one in flight when invalidate() ran) recognize it's been
   *  superseded and skip writing its stale answer over a fresher one. */
  generation: Map<string, number>;
  /** Resolves to null when this call lost the race to a newer request for
   *  the same key and the cache doesn't have that newer request's answer
   *  yet — callers should leave whatever they're already showing alone. */
  fetchAttempts: (
    sessionId: string,
    slideIdx: number,
  ) => Promise<SlideAttempt[] | null>;
  /** Drop the cached entry so the next fetchAttempts call hits the network. */
  invalidate: (sessionId: string, slideIdx: number) => void;
};

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const getCacheKey = (sessionId: string, slideIdx: number) =>
  `${sessionId}:${slideIdx}`;

export const useBiteAttemptsStore = create<BiteAttemptsState>((set, get) => ({
  cache: new Map(),
  generation: new Map(),

  fetchAttempts: async (sessionId: string, slideIdx: number) => {
    const key = getCacheKey(sessionId, slideIdx);
    const state = get();

    // Check cache first
    const cached = state.cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }

    // Always issue a fresh request rather than piggy-backing on one already
    // in flight: a caller arriving right after invalidate() must not be
    // handed the answer of a request that started before the bite resolved.
    const myGeneration = (state.generation.get(key) ?? 0) + 1;
    set((s) => ({
      generation: new Map(s.generation).set(key, myGeneration),
    }));
    const isCurrent = () => get().generation.get(key) === myGeneration;

    try {
      // Demo accounts read a frozen capture instead — see service/demoAnalytics.
      const payload = isDemoAccount("slide-attempts")
        ? await loadDemoSlideAttempts(sessionId, slideIdx)
        : (
            await api.get(
              `/analytics/sessions/${sessionId}/bites/${slideIdx}/slide-attempts`,
            )
          ).data;
      const data: SlideAttempt[] = Array.isArray(payload) ? payload : [];

      // A newer request for this key may have started (and even finished)
      // while this one was in flight. Only the current generation writes the
      // cache and gets to hand back its own answer — a slow, superseded
      // response instead defers to whatever the cache holds: the winner's
      // answer if it already landed, or null if it hasn't yet (meaning
      // "nothing authoritative to show — leave the view alone").
      if (isCurrent()) {
        set((s) => {
          const newCache = new Map(s.cache);
          newCache.set(key, { data, timestamp: Date.now() });
          return { cache: newCache };
        });
        return data;
      }
      return get().cache.get(key)?.data ?? null;
    } catch (error) {
      console.error("Failed to fetch bite attempts:", error);
      if (isCurrent()) {
        return [];
      }
      return get().cache.get(key)?.data ?? null;
    }
  },

  invalidate: (sessionId: string, slideIdx: number) => {
    const key = getCacheKey(sessionId, slideIdx);
    set((s) => {
      const newCache = new Map(s.cache);
      newCache.delete(key);
      return {
        cache: newCache,
        generation: new Map(s.generation).set(
          key,
          (s.generation.get(key) ?? 0) + 1,
        ),
      };
    });
  },
}));
